import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileDown } from 'lucide-react';
import { Button } from '@/components/Common/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/Common/dropdown-menu';
import { conversationsService, GeneratedFileFormat } from '@/services/conversations';
import { cn } from '@/lib/utils';
import { GenerateFileDialog, type GenerateFileChoice } from './GenerateFileDialog';

const GENERATED_FILE_FORMATS: { format: GeneratedFileFormat; label: string }[] = [
  { format: 'docx', label: 'Word (.docx)' },
  { format: 'pptx', label: 'PowerPoint (.pptx)' },
  { format: 'pdf', label: 'PDF (.pdf)' },
  { format: 'md', label: 'Markdown (.md)' },
];

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

interface GenerateFileMenuProps {
  /** Active conversation id — required to have content to summarize/export */
  conversationId: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Document-generation menu (docx/pptx/pdf/md) for the active conversation.
 * Lives in the chat header; every format routes through the smart choice dialog
 * (autonomous synthesis vs an org document template).
 */
export const GenerateFileMenu: React.FC<GenerateFileMenuProps> = ({
  conversationId,
  disabled,
  className,
}) => {
  const { t } = useTranslation();
  // The format whose choice dialog is currently open (null = closed). Every
  // format now routes through the smart "template or autonomous" dialog, which
  // owns its own submitting state.
  const [dialogFormat, setDialogFormat] = useState<GeneratedFileFormat | null>(null);

  const handleGenerateFile = (format: GeneratedFileFormat) => {
    setDialogFormat(format);
  };

  // Smart generation from the choice dialog. Throws on failure so the dialog can
  // map the status to a friendly toast and stay open.
  const handleConfirm = async (choice: GenerateFileChoice) => {
    if (!dialogFormat) return;
    const { blob, filename } = await conversationsService.generateConversationFile(conversationId, {
      format: dialogFormat,
      mode: choice.mode,
      template_id: choice.template_id ?? undefined,
      instructions: choice.instructions ?? undefined,
    });
    downloadBlob(blob, filename);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            disabled={disabled}
            size="icon"
            variant="ghost"
            className={cn('shrink-0 text-muted-foreground hover:text-foreground', className)}
            aria-label={t('chatGenerate.button')}
            title={t('chatGenerate.button')}
          >
            <FileDown className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {GENERATED_FILE_FORMATS.map(({ format, label }) => (
            <DropdownMenuItem key={format} onClick={() => handleGenerateFile(format)}>
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {dialogFormat && (
        <GenerateFileDialog
          open
          format={dialogFormat}
          onClose={() => setDialogFormat(null)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
